import { useState, useEffect } from 'react';
import { MarkerAction } from '../../../../types/marker';
import { ActionType } from '../../../../types/action';
import { useAppContext } from '../../../../context/AppContext';
import { v4 as uuidv4 } from 'uuid';
import logger from '../../../../utils/logger';
import { actionsService } from '../../../../services/supabase/actions.service';
import { notificationsService } from '../../../../services/supabase/notifications.service';
import { supabase } from '../../../../config/supabase';
import { Notification } from '../../../../types/notification';

interface UseMarkerActionsProps {
  markerId: string;
  isOpen: boolean;
}

export const useMarkerActions = ({ markerId, isOpen }: UseMarkerActionsProps) => {
  // États consolidés pour la création d'actions
  const [newActionState, setNewActionState] = useState({
    type: '',
    details: '',
    customType: '',
    showNewTypeInput: false,
    reminderDate: null as Date | null,
    tempReminderDate: null as Date | null,
    showDatePicker: false
  });

  // États consolidés pour l'édition d'actions
  const [editActionState, setEditActionState] = useState({
    editingAction: null as MarkerAction | null,
    type: '',
    details: '',
    reminderDate: null as Date | null,
    tempReminderDate: null as Date | null,
    showDatePicker: false
  });

  // Fonctions helper pour maintenir la compatibilité avec l'API existante
  const setNewActionType = (value: string | ((prev: string) => string)) => {
    if (typeof value === 'function') {
      setNewActionState(prev => ({ ...prev, type: value(prev.type) }));
    } else {
      setNewActionState(prev => ({ ...prev, type: value }));
    }
  };
  const setNewActionDetails = (value: string | ((prev: string) => string)) => {
    if (typeof value === 'function') {
      setNewActionState(prev => ({ ...prev, details: value(prev.details) }));
    } else {
      setNewActionState(prev => ({ ...prev, details: value }));
    }
  };
  const setNewCustomType = (value: string | ((prev: string) => string)) => {
    if (typeof value === 'function') {
      setNewActionState(prev => ({ ...prev, customType: value(prev.customType) }));
    } else {
      setNewActionState(prev => ({ ...prev, customType: value }));
    }
  };
  const setShowNewTypeInput = (value: boolean | ((prev: boolean) => boolean)) => {
    if (typeof value === 'function') {
      setNewActionState(prev => ({ ...prev, showNewTypeInput: value(prev.showNewTypeInput) }));
    } else {
      setNewActionState(prev => ({ ...prev, showNewTypeInput: value }));
    }
  };
  const setReminderDate = (value: Date | null | ((prev: Date | null) => Date | null)) => {
    if (typeof value === 'function') {
      setNewActionState(prev => ({ ...prev, reminderDate: value(prev.reminderDate) }));
    } else {
      setNewActionState(prev => ({ ...prev, reminderDate: value }));
    }
  };
  const setTempReminderDate = (value: Date | null | ((prev: Date | null) => Date | null)) => {
    if (typeof value === 'function') {
      setNewActionState(prev => ({ ...prev, tempReminderDate: value(prev.tempReminderDate) }));
    } else {
      setNewActionState(prev => ({ ...prev, tempReminderDate: value }));
    }
  };
  const setShowDatePicker = (value: boolean | ((prev: boolean) => boolean)) => {
    if (typeof value === 'function') {
      setNewActionState(prev => ({ ...prev, showDatePicker: value(prev.showDatePicker) }));
    } else {
      setNewActionState(prev => ({ ...prev, showDatePicker: value }));
    }
  };

  const setEditingAction = (value: MarkerAction | null | ((prev: MarkerAction | null) => MarkerAction | null)) => {
    if (typeof value === 'function') {
      setEditActionState(prev => ({ ...prev, editingAction: value(prev.editingAction) }));
    } else {
      setEditActionState(prev => ({ ...prev, editingAction: value }));
    }
  };
  const setEditActionType = (value: string | ((prev: string) => string)) => {
    if (typeof value === 'function') {
      setEditActionState(prev => ({ ...prev, type: value(prev.type) }));
    } else {
      setEditActionState(prev => ({ ...prev, type: value }));
    }
  };
  const setEditActionDetails = (value: string | ((prev: string) => string)) => {
    if (typeof value === 'function') {
      setEditActionState(prev => ({ ...prev, details: value(prev.details) }));
    } else {
      setEditActionState(prev => ({ ...prev, details: value }));
    }
  };
  const setEditReminderDate = (value: Date | null | ((prev: Date | null) => Date | null)) => {
    if (typeof value === 'function') {
      setEditActionState(prev => ({ ...prev, reminderDate: value(prev.reminderDate) }));
    } else {
      setEditActionState(prev => ({ ...prev, reminderDate: value }));
    }
  };
  const setEditTempReminderDate = (value: Date | null | ((prev: Date | null) => Date | null)) => {
    if (typeof value === 'function') {
      setEditActionState(prev => ({ ...prev, tempReminderDate: value(prev.tempReminderDate) }));
    } else {
      setEditActionState(prev => ({ ...prev, tempReminderDate: value }));
    }
  };
  const setShowEditDatePicker = (value: boolean | ((prev: boolean) => boolean)) => {
    if (typeof value === 'function') {
      setEditActionState(prev => ({ ...prev, showDatePicker: value(prev.showDatePicker) }));
    } else {
      setEditActionState(prev => ({ ...prev, showDatePicker: value }));
    }
  };

  // Getters pour maintenir la compatibilité
  const newActionType = newActionState.type;
  const newActionDetails = newActionState.details;
  const newCustomType = newActionState.customType;
  const showNewTypeInput = newActionState.showNewTypeInput;
  const reminderDate = newActionState.reminderDate;
  const tempReminderDate = newActionState.tempReminderDate;
  const showDatePicker = newActionState.showDatePicker;

  const editingAction = editActionState.editingAction;
  const editActionType = editActionState.type;
  const editActionDetails = editActionState.details;
  const editReminderDate = editActionState.reminderDate;
  const editTempReminderDate = editActionState.tempReminderDate;
  const showEditDatePicker = editActionState.showDatePicker;

  const { state, dispatch } = useAppContext();

  // Fonctions utilitaires pour la gestion des notifications
  const getUserInfo = async () => {
    const { data: userData } = await supabase.auth.getUser();
    if (!userData || !userData.user) {
      throw new Error('Utilisateur non authentifié');
    }

    const { data: userProfile } = await supabase
      .from('users')
      .select('agency_id')
      .eq('id', userData.user.id)
      .single();

    return {
      userId: userData.user.id,
      agencyId: userProfile?.agency_id
    };
  };

  const createNotificationFromAction = async (action: MarkerAction, userInfo: { userId: string; agencyId: string }) => {
    if (!action.reminderDate) return null;

    // Récupérer les informations du marqueur directement depuis Supabase
    const { data: markerData, error: markerError } = await supabase
      .from('markers')
      .select('address, category_id')
      .eq('id', action.markerId)
      .single();
    
    if (markerError) {
      logger.error(`Erreur lors de la récupération du marqueur ${action.markerId}:`, markerError);
      throw new Error(`Impossible de récupérer le marqueur ${action.markerId}`);
    }
    
    const address = markerData?.address || '';
    const categoryId = markerData?.category_id || null;
    
    // Récupérer le nom de la catégorie si nécessaire
    let categoryName = 'Non catégorisé';
    if (categoryId) {
      const { data: categoryData } = await supabase
        .from('categories')
        .select('name')
        .eq('id', categoryId)
        .single();
      
      categoryName = categoryData?.name || 'Non catégorisé';
    }

    const notification: Omit<Notification, 'id'> = {
      title: `Rappel: ${action.type}`,
      message: action.description || 'Action programmée',
      type: 'reminder',
      status: 'unread',
      actionId: action.id,
      markerId: action.markerId,
      markerCategory: categoryName,
      address: address,
      isActive: true,
      user_id: userInfo.userId,
      agency_id: userInfo.agencyId,
      reminderDate: action.reminderDate ? (action.reminderDate instanceof Date ? action.reminderDate : new Date(action.reminderDate)) : undefined,
      createdAt: new Date()
    };

    logger.debug('Création de notification pour l\'action:', {
      actionId: action.id,
      scheduledDate: action.reminderDate,
      userId: userInfo.userId,
      agencyId: userInfo.agencyId,
      address,
      categoryName
    });

    return await notificationsService.create(notification);
  };



  // Charger les types d'actions personnalisées
  useEffect(() => {
    const loadCustomActionTypes = async () => {
      try {
        const customTypes = await actionsService.getCustomActionTypes();
        logger.debug('Types d\'actions personnalisées chargés:', customTypes);
        
        if (customTypes.length > 0) {
          // Mettre à jour le state global avec les types d'actions personnalisées
          dispatch({ type: 'SET_CUSTOM_ACTION_TYPES', payload: customTypes });
        }
      } catch (error) {
        logger.error('Erreur lors du chargement des types d\'actions personnalisées:', error);
      }
    };

    if (isOpen) {
      loadCustomActionTypes();
    }
  }, [isOpen, dispatch]);

  // Charger les actions temporaires
  useEffect(() => {
    if (isOpen && markerId) {
      // Récupérer les actions temporaires depuis Supabase
      const loadTemporaryActions = async () => {
        try {
          const tempActions = await actionsService.getTemporaryActionsByMarkerId(markerId);
          
          if (tempActions.length > 0) {
            logger.info(`Chargement de ${tempActions.length} actions temporaires depuis Supabase pour le marqueur ${markerId}`);
            return tempActions;
          }
          return [];
        } catch (error) {
          logger.error('Erreur lors du chargement des actions temporaires:', error);
          return [];
        }
      };
      
      loadTemporaryActions();
    }
  }, [isOpen, markerId]);

  // Fonction pour créer une action personnalisée
  const createCustomAction = (type: string, description: string, reminderDate?: Date): MarkerAction => {
    return {
      id: uuidv4(),
      markerId: markerId,
      date: new Date(),
      type: type as ActionType,
      description,
      status: 'pending',
      is_system: false,
      reminderDate
    };
  };

  // Fonction pour fusionner des actions
  const mergeActions = (existingActions: MarkerAction[], newAction: MarkerAction): MarkerAction[] => {
    return [...existingActions, newAction];
  };

  // Fonction pour créer une action de changement de catégorie
  const createCategoryChangeAction = (oldCategoryId: string | null, newCategoryId: string | null): MarkerAction => {
    const oldCategoryName = oldCategoryId 
      ? state.categories.find(cat => cat.id === oldCategoryId)?.name || 'Sans catégorie'
      : 'Sans catégorie';
    
    const newCategoryName = newCategoryId
      ? state.categories.find(cat => cat.id === newCategoryId)?.name || 'Sans catégorie'
      : 'Sans catégorie';

    return {
      id: uuidv4(),
      markerId: markerId,
      date: new Date(),
      type: 'category_change',
      description: `${oldCategoryName} → ${newCategoryName}`,
      status: 'completed',
      is_system: true
    };
  };

  // Fonction pour ajouter une nouvelle action
  const addNewAction = async (type: string, description: string, reminderDate: Date | null = null) => {
    try {
      const newAction = createCustomAction(type, description, reminderDate || undefined);
      
      logger.info('🔍 DEBUG: === DÉBUT CRÉATION ACTION ===');
      logger.debug('Création d\'une nouvelle action:', { 
        id: newAction.id, 
        type, 
        description, 
        reminderDate: reminderDate?.toISOString(), 
        markerId 
      });
      
      // Utiliser la fonction utilitaire pour récupérer les informations utilisateur
      const userInfo = await getUserInfo();
      
      logger.debug('Informations utilisateur pour la création d\'action:', userInfo);
      
      const actionToCreate: Omit<MarkerAction, 'id'> & { agency_id?: string | null } = {
        type: type as ActionType,
        description,
        reminderDate: reminderDate || undefined,
        markerId,
        status: 'pending' as 'pending',
        date: new Date(),
        is_system: false,
        user_id: userInfo.userId,
        agency_id: userInfo.agencyId
      };
      
      logger.debug('Données d\'action prêtes à être envoyées:', actionToCreate);
      
      // Créer l'action directement avec le service simplifié
      const createdAction = await actionsService.create(actionToCreate);
      
      logger.debug('Action créée avec succès dans Supabase:', { 
        actionId: createdAction.id,
        userId: userInfo.userId,
        agencyId: userInfo.agencyId
      });
      
      logger.info('🔍 DEBUG: Action créée avec succès dans Supabase');
      logger.info('🔍 DEBUG: === FIN CRÉATION ACTION ===');
      
      // Si une date de rappel est spécifiée, créer une notification
      if (reminderDate && createdAction) {
        logger.debug('Date de rappel spécifiée, création d\'une notification...');
        
        try {
          // Créer l'action avec l'ID réel pour la notification
          const actionWithRealId: MarkerAction = {
            ...newAction,
            id: createdAction.id // Utiliser l'ID réel retourné par Supabase
          };
          
          // Utiliser la fonction utilitaire pour créer la notification
          logger.debug('Création de la notification avec la fonction utilitaire...');
          const createdNotification = await createNotificationFromAction(actionWithRealId, userInfo);
          
          if (createdNotification) {
            logger.info('Notification créée avec succès:', createdNotification);
            // L'événement NOTIFICATION_CREATED est déjà émis dans notificationsService.create
            logger.debug('Détails de la notification créée:', createdNotification);
          }
        } catch (notificationError) {
          logger.error('Erreur globale lors de la création de la notification de rappel:', notificationError);
          if (notificationError instanceof Error) {
            logger.error('Message d\'erreur:', notificationError.message);
            logger.error('Stack trace:', notificationError.stack);
          }
          // Ne pas bloquer la création de l'action si la notification échoue
        }
      }
      
      return createdAction;
    } catch (error) {
      logger.error('Erreur lors de la création de l\'action:', error);
      return null;
    }
  };

  // Fonction pour supprimer une action
  const deleteAction = async (actionId: string) => {
    try {
      if (markerId) {
        await actionsService.delete(actionId);
        logger.info(`Action ${actionId} supprimée avec succès`);
        return true;
      }
      return false;
    } catch (error) {
      logger.error('Erreur lors de la suppression de l\'action:', error);
      return false;
    }
  };

  // Fonction pour mettre à jour une action
  const updateAction = async (action: MarkerAction) => {
    try {
      if (markerId) {
        await actionsService.update(action.id, action); // Correction de l'erreur TS2554 en passant l'ID comme premier argument
        logger.info(`Action ${action.id} mise à jour avec succès`);
        
        // Si l'action a une date de rappel, créer ou mettre à jour la notification
        if (action.reminderDate) {
          try {
            logger.debug('Tentative de création/mise à jour de notification pour une action modifiée avec reminderDate:', { 
              actionId: action.id, 
              markerId, 
              reminderDate: action.reminderDate instanceof Date ? 
                action.reminderDate.toISOString() : 
                new Date(action.reminderDate).toISOString() 
            });
            
            // Récupérer les informations utilisateur avec la fonction utilitaire
            const userInfo = await getUserInfo();
            
            logger.debug('Informations utilisateur pour la création/mise à jour de notification:', userInfo);
            
            // Vérifier s'il existe déjà une notification pour cette action
            const notifications = await notificationsService.getAll();
            const existingNotification = notifications.find(n => n.actionId === action.id);
            
            if (existingNotification) {
              logger.debug(`Notification existante trouvée (id: ${existingNotification.id}), mise à jour...`);
              // Créer une nouvelle notification avec les données mises à jour et l'utiliser pour la mise à jour
              const updatedNotificationData = await createNotificationFromAction(action, userInfo);
              if (updatedNotificationData) {
                await notificationsService.update(existingNotification.id, {
                  ...updatedNotificationData,
                  id: existingNotification.id
                });
                logger.info(`Notification ${existingNotification.id} mise à jour avec succès`);
              }
            } else {
              logger.debug('Aucune notification existante, création d\'une nouvelle...');
              const createdNotification = await createNotificationFromAction(action, userInfo);
              if (createdNotification) {
                logger.info(`Nouvelle notification créée pour l'action ${action.id}`);
              }
            }
          } catch (notificationError) {
            logger.error('Erreur lors de la gestion de la notification:', notificationError);
            logger.warn('La mise à jour de l\'action a réussi, mais la gestion de la notification a échoué');
          }
        }
        
        return true;
      }
      return false;
    } catch (error) {
      logger.error('Erreur lors de la mise à jour de l\'action:', error);
      return false;
    }
  };

  return {
    // États
    newActionType,
    setNewActionType,
    newActionDetails,
    setNewActionDetails,
    newCustomType,
    setNewCustomType,
    showNewTypeInput,
    setShowNewTypeInput,
    reminderDate,
    setReminderDate,
    tempReminderDate,
    setTempReminderDate,
    showDatePicker,
    setShowDatePicker,
    editingAction,
    setEditingAction,
    editActionType,
    setEditActionType,
    editActionDetails,
    setEditActionDetails,
    editReminderDate,
    setEditReminderDate,
    editTempReminderDate,
    setEditTempReminderDate,
    showEditDatePicker,
    setShowEditDatePicker,
    
    // Fonctions
    createCustomAction,
    mergeActions,
    createCategoryChangeAction,
    addNewAction,
    deleteAction,
    updateAction
  };
};

